// doubtsolvingmode.js
import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import './doubtsolvingmode.css';
import { useLanguage } from '../contexts/languagecontext';
import { API_BASE, authFetch } from '../services/api.js';
import { formatTextContent } from '../utils/textformatting';
import '../utils/textformatting.css';
import CustomSelect from '../components/CustomSelect';


function DoubtSolvingMode() {
  const [question, setQuestion] = useState('');
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [followUpCount, setFollowUpCount] = useState(0);  // ADD THIS
  const [limitReached, setLimitReached] = useState(false);  // ADD THIS
  // ADD THIS LINE:
  const conversationEndRef = React.useRef(null);

  // Auto-scroll to bottom when conversation updates
  React.useEffect(() => {
    if (conversation.length > 0) {
      conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);
  
  // ADD THE NEW LINE HERE
  const { currentLanguage, switchLanguage } = useLanguage();
  
  
  // Filtering states
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState('');
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState('');
  
  

  // Dummy student ID (in real app, get from auth)
  const userData = JSON.parse(localStorage.getItem('userData'));
  const STUDENT_ID = userData?.student?.student_id || 1;

  // Load subjects on component mount
  useEffect(() => {
    loadSubjects();
  }, []);

  // Load chapters when subject changes
  useEffect(() => {
    if (selectedSubject) {
      loadChapters(selectedSubject);
      setSelectedChapter('');
      setSelectedTopic('');
    }
  }, [selectedSubject]);

  // Load topics when chapter changes
  useEffect(() => {
    if (selectedChapter) {
      loadTopics(selectedChapter, selectedSubject);
      setSelectedTopic('');
    }
  }, [selectedChapter, selectedSubject]);

  const loadSubjects = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      const studentId = userData?.student?.student_id;
      const subjectsData = await ApiService.getSubjects(studentId);
      setSubjects(subjectsData);
    } catch (error) {
      console.error('Failed to load subjects:', error);
    }
  };

  const loadChapters = async (subjectId) => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      const studentId = userData?.student?.student_id;
      const response = await authFetch(`${API_BASE}/chapters/?subject_id=${subjectId}&student_id=${studentId}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const chaptersData = await response.json();
      setChapters(chaptersData);
    } catch (error) {
      console.error('Failed to load chapters:', error);
    }
  };

  const loadTopics = async (chapterId, subjectId) => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      const studentId = userData?.student?.student_id;
      const response = await authFetch(`${API_BASE}/topics/?chapter_id=${chapterId}&subject_id=${subjectId}&student_id=${studentId}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const topicsData = await response.json();
      setTopics(topicsData);
    } catch (error) {
      console.error('Failed to load topics:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSubject) {
      alert('Please select a subject first');
      return;
    }
    
    if (question.trim() && !loading && !limitReached) {
      // For new session, lock the language
      if (!currentSessionId) {
        switchLanguage(currentLanguage);
      }
      
      const userMessage = {
        type: 'question',
        text: question,
        timestamp: new Date().toLocaleTimeString()
      };
      
      setConversation(prev => [...prev, userMessage]);
      setLoading(true);
      
      try {
        const doubtResponse = await ApiService.askDoubt(
          question,
          currentSessionId,
          STUDENT_ID,
          parseInt(selectedSubject),
          selectedChapter ? parseInt(selectedChapter) : null,
          selectedTopic ? parseInt(selectedTopic) : null,
          currentLanguage
        );

        // Update session ID and follow-up counter
        // Update session ID and follow-up counter
        setCurrentSessionId(doubtResponse.session_id);
        setFollowUpCount(doubtResponse.follow_up_count);
        setLimitReached(doubtResponse.follow_up_limit_reached);
        
        // Add AI response to conversation
        const aiMessage = {
          type: 'answer',
          text: doubtResponse.answer,
          timestamp: new Date().toLocaleTimeString(),
          ragUsed: doubtResponse.rag_used,
          searchResultsCount: doubtResponse.search_results_count
        };
        
        setConversation(prev => [...prev, aiMessage]);
        
      } catch (error) {
        // Check if it's the 429 (limit reached) error
        if (error.response && error.response.status === 429) {
          setLimitReached(true);
          const errorMessage = {
            type: 'system',
            text: currentLanguage === 'hi'
              ? '⚠️ आपने अपनी फॉलो-अप सीमा पूरी कर ली है। बेहतर स्पष्टीकरण के लिए कृपया एक नया doubt प्रश्न शुरू करें।'
              : '⚠️ You\'ve reached the follow-up limit. Please start a new doubt question for better explanation.',
            timestamp: new Date().toLocaleTimeString()
          };
          setConversation(prev => [...prev, errorMessage]);
        } else {
          const errorMessage = {
            type: 'answer',
            text: currentLanguage === 'hi' 
              ? 'मुझे खुशी है कि आपने सवाल पूछा, लेकिन अभी कुछ तकनीकी समस्या है। कृपया दोबारा कोशिश करें।'
              : 'Sorry, I encountered an error while processing your question. Please try again.',
            timestamp: new Date().toLocaleTimeString()
          };
          setConversation(prev => [...prev, errorMessage]);
        }
      }
      
      setQuestion('');
      setLoading(false);
    }
  };

  

  const startNewSession = async () => {
    // Complete the current session in backend if exists
    if (currentSessionId) {
      try {
        await ApiService.completeDoubtSession(currentSessionId, STUDENT_ID);
      } catch (error) {
        console.error('Failed to complete session:', error);
        // Continue anyway to clear frontend
      }
    }
    
    // Clear everything
    setConversation([]);
    setCurrentSessionId(null);
    setFollowUpCount(0);
    setLimitReached(false);
    setQuestion('');
    // Note: NOT resetting language - keep user's language preference
  };

  return (
    <div className="doubt-solving-mode">
      <div className="container">
        <h1>❓ Doubt Solving Mode</h1>
        
        {/* Filtering Section */}
        <div className="filters-section">
          <div className="filter-row">
            <CustomSelect
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              placeholder="Select Subject *"
              disabled={!!currentSessionId}
              options={subjects
                .filter(subject => {
                  const studentData = JSON.parse(localStorage.getItem('userData') || '{}');
                  const accessibleSubjects = studentData.student?.accessible_subjects || [];
                  return accessibleSubjects.some(accSubj => accSubj.subject_id === subject.subject_id);
                })
                .map(subject => ({ value: subject.subject_id, label: subject.name }))
              }
            />
            
            <CustomSelect
              value={selectedChapter}
              onChange={(e) => setSelectedChapter(e.target.value)}
              placeholder="All Chapters (Optional)"
              disabled={!selectedSubject || !!currentSessionId}
              options={chapters.map(chapter => ({ value: chapter.chapter_id, label: chapter.name }))}
            />
            
            <CustomSelect
              value={selectedTopic}
              onChange={(e) => setSelectedTopic(e.target.value)}
              placeholder="All Topics (Optional)"
              disabled={!selectedChapter || !!currentSessionId}
              options={topics.map(topic => ({ value: topic.topic_id, label: topic.name }))}
            />
            
            {/* Language Selection - only for new sessions */}
            <CustomSelect
              value={currentLanguage}
              onChange={(e) => switchLanguage(e.target.value)}
              placeholder="Select Language"
              disabled={!!currentSessionId}
              options={[
                { value: 'en', label: '🇺🇸 English' },
                { value: 'hi', label: '🇮🇳 हिंदी' }
              ]}
            />
          </div>
        </div>
        
        


        {/* New Question Button - Shows after first answer */}
        {currentSessionId && conversation.length >= 2 && (
          <div className="new-question-control" style={{
            marginBottom: '12px',
            textAlign: 'center'
          }}>
            <button 
              onClick={startNewSession} 
              className="new-question-button"
              style={{
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              📝 Start New Question
            </button>
          </div>
        )}
        
        <div className="chat-container">
          <div className="conversation">
            {conversation.length === 0 ? (
              <div className="welcome-message" style={{ padding: '20px', minHeight: 'auto' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>👋 Select a subject and language, then ask your question!</h3>
                <p style={{ margin: 0, fontSize: '14px' }}>I'll give you focused, concise answers from that subject</p>
              </div>
            ) : (
              conversation.map((item, index) => (
                <div key={index} className={`message ${item.type}`}>
                  <div className="message-content">
                    <div style={{whiteSpace: 'pre-wrap'}}>{formatTextContent(item.text)}</div>
                    <div className="message-meta">
                      
                    </div>
                  </div>
                </div>
              ))
            )}
            
            {loading && (
              <div className="message answer">
                <div className="message-content">
                  <p>🤔 {currentLanguage === 'hi' ? 'सोच रहा हूँ...' : 'Thinking...'}</p>
                </div>
              </div>
            )}
            
            {/* ADD THIS - Invisible element for auto-scroll */}
            <div ref={conversationEndRef} />
          </div>
          
          {/* ADD THIS NOTIFICATION BANNER for one original question + 5 follow up questions limit reached*/}
         {/* Limit notification - shows just above input */}
          {limitReached && (
            <div style={{
              background: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: '4px',
              padding: '8px 12px',
              marginBottom: '8px',
              fontSize: '13px',
              color: '#92400e',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>⚠️</span>
              <span>
                {currentLanguage === 'hi'
                  ? 'फॉलो-अप सीमा पूर्ण। बेहतर स्पष्टीकरण के लिए नया प्रश्न शुरू करें।'
                  : 'Follow-up limit reached. Start a new question for better explanation.'}
              </span>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="question-form">
            <div className="input-group">
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder={
                  limitReached
                    ? (currentLanguage === 'hi' ? "सीमा पूर्ण - नया प्रश्न शुरू करें" : "Limit reached - Start new question")
                    : selectedSubject 
                    ? (currentLanguage === 'hi' ? "अपना सवाल पूछें..." : "Ask your question...")
                    : "Select a subject first"
                }
                className="question-input"
                disabled={loading || !selectedSubject || limitReached}
              />
              <button 
                type="submit" 
                className="submit-button" 
                disabled={loading || !selectedSubject || limitReached}
                style={{
                  opacity: (loading || !selectedSubject || limitReached) ? 0.5 : 1,
                  cursor: (loading || !selectedSubject || limitReached) ? 'not-allowed' : 'pointer'
                }}
              >
                {loading 
                  ? (currentLanguage === 'hi' ? 'भेज रहा हूँ...' : 'Sending...') 
                  : (currentLanguage === 'hi' ? 'भेजें' : 'Send')
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default DoubtSolvingMode;